import React, { useEffect, useState } from 'react';
import { fetchTerminalsList, updateTerminal, fetchPortsList } from './services/api';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Form, Card, Alert, Container, Spinner } from 'react-bootstrap';
import { Terminal, Port } from './types';
import { useAuth } from './AuthContext';

const EditTerminal: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [terminal, setTerminal] = useState<Terminal | null>(null);
  const [name, setName] = useState('');
  const [port, setPort] = useState<number | ''>('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [geofenceArea, setGeofenceArea] = useState('');
  const [ports, setPorts] = useState<Port[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchTerminalsList(),
      fetchPortsList()
    ]).then(([terminalsResponse, portsResponse]) => {
      const terminals = Array.isArray(terminalsResponse.data) ? terminalsResponse.data : [];
      const ports = Array.isArray(portsResponse.data) ? portsResponse.data : [];

      const found = terminals.find(t => t.id === Number(id));
      if (!found) throw new Error('Not found');

      setTerminal(found);
      setName(found.name);
      setPort(found.port);
      setLatitude(found.latitude.toString());
      setLongitude(found.longitude.toString());
      setGeofenceArea(found.geofence_area.toString());
      setPorts(ports);
    }).catch(() => setError('Failed to load terminal')).finally(() => setLoading(false));
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await updateTerminal(Number(id), {
        name,
        port: Number(port),
        latitude: Number(latitude),
        longitude: Number(longitude),
        geofence_area: Number(geofenceArea)
      });
      setSuccess(true);
      setTimeout(() => navigate('/terminals'), 1200);
    } catch (err) {
      setError('Failed to update terminal');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}><Spinner animation="border" /></div>;
  if (error) return <Container className="p-4 p-md-5"><Alert variant="danger">{error}</Alert></Container>;
  if (!terminal) return <Container className="p-4 p-md-5">Terminal not found.</Container>;

  return (
    <Container className="p-4 p-md-5" style={{ maxWidth: 500 }}>
      <Card className="shadow-lg mt-4">
        <Card.Body>
          <h2 className="mb-4">Edit Terminal</h2>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="name">
              <Form.Label>Name</Form.Label>
              <Form.Control type="text" value={name} onChange={e => setName(e.target.value)} required maxLength={50} />
            </Form.Group>
            <Form.Group className="mb-3" controlId="port">
              <Form.Label>Location</Form.Label>
              <Form.Select value={port} onChange={e => setPort(e.target.value === '' ? '' : Number(e.target.value))} required disabled={!!(token && token.designation?.user_type === 'ADMIN')}>
                <option value="">Select Location</option>
                {ports.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3" controlId="latitude">
              <Form.Label>Latitude</Form.Label>
              <Form.Control type="number" value={latitude} onChange={e => setLatitude(e.target.value)} required step="any" />
            </Form.Group>
            <Form.Group className="mb-3" controlId="longitude">
              <Form.Label>Longitude</Form.Label>
              <Form.Control type="number" value={longitude} onChange={e => setLongitude(e.target.value)} required step="any" />
            </Form.Group>
            <Form.Group className="mb-4" controlId="geofenceArea">
              <Form.Label>Geofence Area (meters)</Form.Label>
              <Form.Control type="number" value={geofenceArea} onChange={e => setGeofenceArea(e.target.value)} required min={1} />
            </Form.Group>
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">Terminal updated!</Alert>}
            <div className="d-flex justify-content-between">
              <Button type="button" variant="secondary" onClick={() => navigate('/terminals')} disabled={saving}>Back to List</Button>
              <Button type="submit" variant="primary" disabled={saving}>{saving ? <Spinner size="sm" animation="border" /> : 'Save Changes'}</Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default EditTerminal; 